import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { RefreshCw, Users, Eye, TrendingUp, Loader2, Clock, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { formatNyDateTime } from '../../utils/datetime';

const PERIOD_LABELS = { today: 'Hoje', week: 'Esta Semana', month: 'Este Mês' };
const MEDALS = ['🥇', '🥈', '🥉'];

const ACTION_ICON = (action = '') => {
  const a = (action || '').toLowerCase();
  if (a === 'login') return '🔓';
  if (a === 'app_access') return '📲';
  if (a === 'navigate') return '🧭';
  if (a.includes('arbitration')) return '⚖️';
  if (a.includes('bill')) return '🧾';
  if (a.includes('payroll')) return '💰';
  if (a.includes('email') || a.includes('send')) return '✉️';
  if (a.includes('delete') || a.includes('remove') || a.includes('empty') || a.includes('cleanup') || a.includes('clear')) return '🗑️';
  if (a.includes('ticket')) return '🎫';
  if (a.includes('password')) return '🔑';
  if (a.includes('upload') || a.includes('import')) return '⬆️';
  if (a.includes('download') || a.includes('pdf') || a.includes('generate')) return '⬇️';
  if (a.includes('create') || a.includes('add') || a.includes('configure')) return '➕';
  if (a.includes('edit') || a.includes('update') || a.includes('recalculate') || a.includes('standardize') || a.includes('mark') || a.includes('save')) return '✏️';
  if (a.includes('view') || a.includes('sign')) return '👁️';
  if (a.includes('transfer') || a.includes('divide')) return '🔀';
  return '•';
};

const describeEvent = (ev) => {
  const a = (ev.action || '').toLowerCase();
  if (a === 'login') return 'Fez login';
  if (a === 'app_access') return 'Abriu o app';
  return ev.description || ev.action || 'Ação';
};

const fmtDateTimeFull = (iso) => {
  if (!iso) return '—';
  try { return formatNyDateTime(iso); } catch { return iso; }
};
const fmtTimeOnly = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};
const fmtDateOnly = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' }); } catch { return ''; }
};
const fmtDuration = (secs) => {
  if (!secs || secs < 60) return '< 1 min';
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
};

const roleLabel = (r) => ({
  user: 'PM', daily_leader: 'Líder', personal: 'Pessoal', general_manager: 'Gerente',
}[r] || r || 'Usuário');

const fmtDateShort = (iso) => {
  try {
    const [, m, d] = iso.split('-');
    return `${parseInt(d, 10)}/${parseInt(m, 10)}`;
  } catch { return iso; }
};

const fmtLastAccess = (iso) => {
  if (!iso) return '—';
  try {
    return formatNyDateTime(iso);
  } catch { return '—'; }
};

const SummaryCard = ({ testid, label, value, sub, accent, Icon }) => (
  <div data-testid={testid} className={`rounded-xl border p-4 bg-white shadow-sm ${accent}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      {Icon && <Icon className="w-4 h-4 text-slate-400" />}
    </div>
    <div className="mt-2 text-3xl font-bold text-slate-800" data-testid={`${testid}-value`}>{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

export default function AccessMonitor({ API }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('today');

  // Histórico de acessos (sessões reconstruídas)
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [filterUser, setFilterUser] = useState('');
  const [filterPeriod, setFilterPeriod] = useState(365);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('access_token');
      const { data } = await axios.get(`${API}/admin/access-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (e) {
      console.error('Erro ao carregar estatísticas de acesso:', e);
      setError('Não foi possível carregar as estatísticas de acesso.');
    } finally {
      setLoading(false);
    }
  }, [API]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const { data } = await axios.get(`${API}/admin/access-sessions`, {
        params: { days: 365 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(data.sessions || []);
    } catch (e) {
      console.error('Erro ao carregar histórico de acessos:', e);
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const ranking = data?.[period]?.ranking || [];
  const series = (data?.series_14d || []).map((s) => ({ ...s, label: fmtDateShort(s.date) }));

  // Filtros do Histórico (client-side sobre as sessões já carregadas)
  const uniqueUsers = Array.from(new Map(sessions.map((s) => [s.user, s.full_name])).entries())
    .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
  const nowMs = Date.now();
  const filteredSessions = sessions.filter((s) => {
    if (filterUser && s.user !== filterUser) return false;
    if (filterPeriod && filterPeriod < 365) {
      const startMs = new Date(s.start).getTime();
      if (!Number.isNaN(startMs) && (nowMs - startMs) > filterPeriod * 24 * 3600 * 1000) return false;
    }
    return true;
  });

  return (
    <div className="space-y-5" data-testid="access-monitor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" /> Controle de Acessos
          </h3>
          <p className="text-xs text-slate-500">Monitor silencioso — os usuários não são notificados. (Admin não é contabilizado.)</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { load(); loadSessions(); }} disabled={loading} data-testid="access-refresh-btn" className="text-xs">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1">Atualizar</span>
        </Button>
      </div>

      {error && <div className="text-sm text-red-600" data-testid="access-error">{error}</div>}

      {loading && !data && (
        <div className="flex items-center justify-center py-12 text-slate-500" data-testid="access-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando…
        </div>
      )}

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="access-summary">
            <SummaryCard testid="access-card-today" label="Acessos Hoje" value={data.today.total}
              sub={`${data.today.unique} usuário(s) ativo(s)`} accent="border-indigo-200" Icon={Eye} />
            <SummaryCard testid="access-card-week" label="Esta Semana" value={data.week.total}
              sub={`${data.week.unique} usuário(s) ativo(s)`} accent="border-emerald-200" Icon={Users} />
            <SummaryCard testid="access-card-month" label="Este Mês" value={data.month.total}
              sub={`${data.month.unique} usuário(s) ativo(s)`} accent="border-amber-200" Icon={Users} />
            <SummaryCard testid="access-card-total" label="Total (histórico)" value={data.total_all}
              sub="desde o início do rastreamento" accent="border-slate-200" Icon={TrendingUp} />
          </div>

          {/* Chart */}
          <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid="access-chart">
            <div className="text-sm font-semibold text-slate-700 mb-3">Acessos nos últimos 14 dias</div>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={series} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip
                    cursor={{ fill: 'rgba(99,102,241,0.08)' }}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                    labelFormatter={(l) => `Dia ${l}`}
                    formatter={(v) => [`${v} acesso(s)`, 'Acessos']}
                  />
                  <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={38} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Ranking */}
          <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid="access-ranking">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-sm font-semibold text-slate-700">Ranking de Acessos</div>
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                {['today', 'week', 'month'].map((p) => (
                  <button
                    key={p}
                    data-testid={`access-period-${p}`}
                    onClick={() => setPeriod(p)}
                    className={`text-xs px-3 py-1 rounded-md transition-colors ${period === p ? 'bg-white shadow text-indigo-700 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {PERIOD_LABELS[p]}
                  </button>
                ))}
              </div>
            </div>

            {ranking.length === 0 ? (
              <div className="text-center text-slate-400 py-8 text-sm" data-testid="access-ranking-empty">
                Nenhum acesso registrado neste período ainda.
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="access-ranking-list">
                {ranking.map((r, idx) => (
                  <div
                    key={r.user}
                    data-testid={`access-rank-row-${r.user}`}
                    className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-base w-6 text-center flex-shrink-0">{MEDALS[idx] || <span className="text-slate-400 text-sm">{idx + 1}</span>}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">
                          {r.full_name} <span className="text-xs text-slate-400">({r.user})</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          {roleLabel(r.role)} · último acesso {fmtLastAccess(r.last_access)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 pl-2">
                      <div className="text-base font-bold text-indigo-600" data-testid={`access-rank-count-${r.user}`}>{r.count}</div>
                      <div className="text-[10px] text-slate-400 uppercase">acessos</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Histórico de acessos (todos os usuários, cronológico) */}
          <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid="access-history">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-500" />
                <span className="text-sm font-semibold text-slate-700">Histórico</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  data-testid="history-filter-user"
                  value={filterUser}
                  onChange={(e) => setFilterUser(e.target.value)}
                  className="border rounded-md px-2 py-1.5 text-xs bg-white text-slate-700 max-w-[180px]"
                >
                  <option value="">Todos os usuários</option>
                  {uniqueUsers.map(([u, name]) => (
                    <option key={u} value={u}>{name} ({u})</option>
                  ))}
                </select>
                <select
                  data-testid="history-filter-period"
                  value={filterPeriod}
                  onChange={(e) => setFilterPeriod(Number(e.target.value))}
                  className="border rounded-md px-2 py-1.5 text-xs bg-white text-slate-700"
                >
                  <option value={1}>Hoje</option>
                  <option value={7}>7 dias</option>
                  <option value={30}>30 dias</option>
                  <option value={90}>90 dias</option>
                  <option value={365}>Tudo (ano)</option>
                </select>
              </div>
            </div>

            {sessionsLoading ? (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando histórico…
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="text-center text-slate-400 py-8 text-sm" data-testid="access-history-empty">
                {sessions.length === 0 ? 'Nenhum acesso registrado ainda.' : 'Nenhum acesso encontrado para o filtro selecionado.'}
              </div>
            ) : (
              <>
                <div className="text-xs text-slate-400 mb-2" data-testid="access-history-count">
                  {filteredSessions.length} acesso(s)
                </div>
                <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto" data-testid="access-history-list">
                  {filteredSessions.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`access-session-row-${s.id}`}
                      onClick={() => setSelectedSession(s)}
                      className="w-full flex items-center justify-between gap-3 py-2.5 px-1 text-left hover:bg-indigo-50/60 rounded-md transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-lg flex-shrink-0">📲</span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">
                            {s.full_name} <span className="text-xs text-slate-400">({s.user})</span>
                          </div>
                          <div className="text-xs text-slate-500">
                            Entrou em {fmtDateTimeFull(s.start)} · ativo {fmtDuration(s.duration_seconds)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 pl-2">
                        <span className="text-[11px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {s.count} ação(ões)
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Popup: detalhes da sessão (tudo que o usuário fez naquele acesso) */}
          <Dialog open={!!selectedSession} onOpenChange={(o) => { if (!o) setSelectedSession(null); }}>
            <DialogContent className="max-w-lg" data-testid="access-session-modal">
              <DialogHeader>
                <DialogTitle className="text-base">
                  {selectedSession?.full_name} <span className="text-sm text-slate-400 font-normal">({selectedSession?.user})</span>
                </DialogTitle>
                <div className="text-xs text-slate-500" data-testid="access-session-header">
                  {selectedSession && (
                    <>Acesso de {fmtDateOnly(selectedSession.start)} · {fmtTimeOnly(selectedSession.start)} → {fmtTimeOnly(selectedSession.end)} · ativo {fmtDuration(selectedSession.duration_seconds)}</>
                  )}
                </div>
              </DialogHeader>
              <div className="mt-1 max-h-[60vh] overflow-y-auto" data-testid="access-session-events">
                {selectedSession && selectedSession.events && selectedSession.events.length > 0 ? (
                  <ol className="relative border-l border-slate-200 ml-2 space-y-3 pl-4 py-1">
                    {selectedSession.events.map((ev, i) => (
                      <li key={i} className="relative" data-testid={`access-session-event-${i}`}>
                        <span className="absolute -left-[22px] top-0.5 text-sm">{ACTION_ICON(ev.action)}</span>
                        <div className="text-sm text-slate-800">{describeEvent(ev)}</div>
                        <div className="text-[11px] text-slate-400">{fmtTimeOnly(ev.timestamp)}</div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="text-center text-slate-400 py-6 text-sm">Sem ações registradas neste acesso.</div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
